import { Fragment } from 'react'

import { BLUEPRINT_2D_DEFAULT_BACKGROUND } from '@editor/config/styles'
import { CanvasConfigObjects, CanvasConfigV2 } from '@editor/interfaces/editor'
import { RootState } from '@editor/store/app'
import { Canvas } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { LinearSRGBColorSpace, NoToneMapping } from 'three'

import SolidMeshManager from '../common/SolidMeshManager'
import CircleMesh from '../editor/objects/CircleMesh'
import { CuboidAnchorFrame } from '../editor/objects/CuboidAnchorFrames'
import CuboidFrame from '../editor/objects/CuboidFrame'
import { CylinderMesh } from '../editor/objects/CylinderMesh'
import CircleAnchor from '../editor/objects/EditorCircleAnchor'
import Label from '../editor/objects/Label'
import { PolygonPlaneMesh } from '../editor/objects/PolygonPlaneMesh'
import PolygonPlaneMeshTransformable from '../editor/objects/PolygonPlaneMeshV2/PolygonMeshTransformable'
import { PolygonPlaneMeshV2 } from '../editor/objects/PolygonPlaneMeshV2/PolygonPlaneMeshV2'
import Polyline from '../editor/objects/Polyline'
import CameraManager from './components/CameraManager'
import CurrentAnchorPosition from './components/CurrentAnchorPosition'
import Drawings2DManager from './components/Drawings2DManager'
import PCDManager from './components/PCDManager'
import { useBlueprintEditorContext } from './contexts/BlueprintEditorContext'
import { Mode } from './definitions'
import useCanvasEvents from './hooks/useCanvasEvents'
import MeasurementLabel from './objects/MeasurementLabel'
import RectangleMesh from './objects/RectangleMesh'
import TextAnnotation from './objects/TextAnnotation'
import { Tools } from './tools/setup'

const MainCanvas = () => {
  // Contexts
  const { pointClouds, activeCamera, sceneRef } = useBlueprintEditorContext()

  // Store
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const customBackgroundColor = useSelector((state: RootState) => state.blueprint.editor.backgroundColor)
  const cursor = useSelector((state: RootState) => state.toolUtil.core.cursor)

  // Vars
  const backgroundColor = mode === Mode.Editor3D ? customBackgroundColor : BLUEPRINT_2D_DEFAULT_BACKGROUND

  // Tools activation by running hook
  const toolHooks = Tools.map((tool) => {
    const tools: {
      key: string
      config: CanvasConfigV2
    }[] = []
    if (tool.hooks?.useMainCanvas) {
      tools.push({
        key: tool.key,
        config: tool.hooks?.useMainCanvas({ pointClouds }),
      })
    }

    if (tool.toolbar?.variants) {
      tool.toolbar.variants.forEach((variant) => {
        if (variant.hooks?.useMainCanvas) {
          tools.push({
            key: `${tool.key}-${variant.key}`,
            config: variant.hooks.useMainCanvas({ pointClouds }),
          })
        }
      })
    }

    return tools
  })
    .flat()
    .filter(Boolean)

  // Initialize hooks
  const { onMouseDown, onMouseMove, onMouseUp } = useCanvasEvents(toolHooks)

  const toolsObjects: {
    key: string
    objects: CanvasConfigObjects | undefined
  }[] = toolHooks
    .map((tool) => ({
      key: tool.key,
      objects: tool?.config.objects,
    }))
    .filter((obj) => obj.objects)

  return (
    <Canvas
      id="canvasBox"
      className={`tooling-${cursor}`}
      style={{ width: '100svw', height: '100%', zIndex: 0, position: 'absolute', top: 0, left: 0 }}
      gl={{
        preserveDrawingBuffer: true,
        localClippingEnabled: false,
        toneMapping: NoToneMapping,
        outputColorSpace: LinearSRGBColorSpace,
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      tabIndex={-1}
    >
      <scene ref={sceneRef}>
        <PCDManager sceneRef={sceneRef} />
        <CameraManager />
        <Drawings2DManager />
        <SolidMeshManager depthTest={false} depthWrite={false} />

        {/* Editor tools objects */}
        {toolsObjects.map((tool, index) => (
          <Fragment key={`tool-objects-${tool.key}`}>
            {tool.objects?.polylines?.map((props) => (
              <Polyline key={`distance-label-single-drawing-${props.id}`} {...props} />
            ))}
            {tool.objects?.labels?.map((warningLabel) => (
              <Label {...warningLabel} />
            ))}
            {tool.objects?.circleAnchors?.map((props) => (
              <CircleAnchor key={`circle-anchor-${props.id}`} customCamera={activeCamera.current} {...props} />
            ))}
            {tool.objects?.circleMeshes?.map((props) => (
              <CircleMesh key={`circle-drawing-${props.id}`} {...props} />
            ))}
            {tool.objects?.rectangleMeshes?.map((props) => (
              <RectangleMesh key={`rectangle-drawing-${props.id}`} {...props} />
            ))}
            {tool.objects?.polygonPlaneMeshes?.map((props) => (
              <PolygonPlaneMesh key={`polygon-drawing-${props.polygon.shape_id}`} {...props} />
            ))}
            {tool.objects?.polygonPlaneMeshesV2?.map((props) => (
              <PolygonPlaneMeshV2 key={`polygon-drawing-${props.polygon.shape_id}`} {...props} />
            ))}
            {tool.objects?.polygonPlaneMeshTransformable?.map((props) => (
              <PolygonPlaneMeshTransformable key={`polygon-drawing-${props.polygon.shape_id}`} {...props} />
            ))}
            {tool.objects?.cylinderMeshes?.map((props) => (
              <CylinderMesh key={`cylinder-drawing-${props.cylinder.shape_id}`} {...props} />
            ))}
            {tool.objects?.textAnnotations?.map((props) => (
              <TextAnnotation key={`text-annotation-${props.id}`} {...props} />
            ))}
            {tool.objects?.measurementLabels?.map((props) => (
              <MeasurementLabel key={`measurement-label-${props.id}`} renderOrder={index + 5} {...props} />
            ))}
            {tool.objects?.cuboidAnchorFrames?.map((props) => (
              <CuboidAnchorFrame key={`cuboid-anchor-frame-${props.id}`} {...props} />
            ))}
            {tool.objects?.cuboidFrames?.map((props) => (
              <CuboidFrame key={`cuboid-frame-${props.id}`} {...props} />
            ))}
          </Fragment>
        ))}

        <CurrentAnchorPosition />

        <color attach="background" args={[backgroundColor]} />
        <ambientLight intensity={Math.PI} />
      </scene>
    </Canvas>
  )
}

export default MainCanvas
